/**
 * @file SessionDetailDialog.tsx
 * @brief Shared session-detail helpers (initials, timestamps, summary item,
 *        flag input, payment pill).
 *
 * @details
 * The side-sheet dialog that lived here was unused since the session page
 * (`SessionDetailPage`, 01-10-2026) and was removed with W4 (22-10-2026): it
 * fetched the staff transcript route, which now answers 403 STAFF_NO_CONTENT.
 * The helpers stay here so their importers need not change.
 *
 * @dependencies
 * - shared/billing/moneyStatus
 *
 * @author DES
 * @date 26-05-2026
 * @updated 22-10-2026 — dialog removed, helpers only (W4)
 */

import type { ReactNode } from "react";
import { disputeLabel, moneyStatus } from "@/shared/billing/moneyStatus";
import { cn } from "@/shared/theme/cn";

export function speakerInitials(name?: string | null): string {
  if (!name || typeof name !== "string") return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Utterance offset for the transcript gutter. Prefers session-relative ms;
 * if the value looks like an absolute epoch (ms), subtract `startedAt`.
 */
export function formatTs(ms?: number | null, startedAt?: string | null): string {
  if (ms == null || !Number.isFinite(ms)) return "";
  let offsetMs = ms;
  const EPOCH_GUARD_MS = 86_400_000; // > 1 day → treat as absolute
  if (offsetMs > EPOCH_GUARD_MS && startedAt) {
    const start = Date.parse(startedAt);
    if (Number.isFinite(start)) {
      offsetMs = Math.max(0, offsetMs - start);
    }
  }
  const totalSec = Math.max(0, Math.floor(offsetMs / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Mobile's summary label/value pair: small caps label over a bold value. */
export function SummaryItem({
  label,
  value,
  align = "start",
}: {
  label: string;
  value: ReactNode;
  align?: "start" | "end";
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-0.5",
        align === "end" && "items-end text-right",
      )}
    >
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="truncate text-[15px] font-bold">{value}</span>
    </div>
  );
}

/** A stored language ("ur" or "ur-PK") as the flag component's input. */
export function flagInput(lang?: string | null) {
  const l = lang ?? "";
  return { code: l.split("-")[0] ?? l, locale: l };
}

/**
 * Mobile's `paymentState`. Returns null when the payload doesn't say (the
 * admin detail route omits `is_paid` / `session_charge`): better no pill than
 * a wrong "Not charged" on a paid session.
 */
export function paymentState(session: {
  is_paid?: boolean | null;
  session_charge?: number | null;
  billing_context?: string | null;
  payment_status?: string | null;
  billed_cents?: number | null;
  captured_cents?: number | null;
  refunded_cents?: number | null;
  payment_failed?: boolean | null;
  is_deleted?: boolean | null;
  dispute_status?: string | null;
}): { label: string; paid: boolean } | null {
  // The server's money status when it sends one (18-10-2026): failed,
  // refunded and part paid read the same here as on Invoices.
  if (session.payment_status) {
    const v = moneyStatus(session);
    const dispute = disputeLabel(v.dispute);
    const label = [v.label, dispute, v.deleted ? "Deleted" : null].filter(Boolean).join(" · ");
    if (v.key === "no_charge" && session.billing_context === "corporate") {
      return { label: "Company billed", paid: false };
    }
    return { label, paid: v.key === "paid" || v.key === "partial" || v.key === "part_refunded" };
  }
  if (session.is_paid == null && session.session_charge == null) return null;
  const paid = session.is_paid ?? (session.session_charge ?? 0) > 0;
  if (paid) return { label: "Paid", paid: true };
  if (session.billing_context === "corporate") {
    return { label: "Company billed", paid: false };
  }
  return { label: "Not charged", paid: false };
}
